import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Paper, Typography } from '@mui/material';
import {
  Cloud as SalesforceIcon,
  ViewKanban as L2Icon,
  Engineering as L3Icon,
  ReportProblem as ServiceNowIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { authGet } from '../../services/apiService';
import { TicketFollowUpPanel } from './top-customers/CustomerDetailView';
import AdoItemDetailsDialog from './AdoItemDetailsDialog';
import SnowTicketDetailsDialog, { SNOW_NUMBER } from './SnowTicketDetailsDialog';
import { AssigneeCell, CaseIdLink, absoluteHttpUrl, isUnassigned } from './assigneeDisplay';
import { formatSfTableDate } from './sfTableFormatting';
import { INCIDENT_ACCENT, INK, MUTED, NEUTRAL_SURFACE, WARN_ORANGE } from './colorTokens';

// Same colors/icons as the L1 Open, L2 Open and L3 Swat tiles on the Vital Signs page.
const STAGE_STYLES = {
  l1: { label: 'L1 · Salesforce', icon: SalesforceIcon, color: WARN_ORANGE, bg: '#fbe9dd' },
  l2: { label: 'L2 · Investigation', icon: L2Icon, color: '#2E7D32', bg: '#eef3f0' },
  l3: { label: 'L3 · Engineering', icon: L3Icon, color: '#b71c1c', bg: '#fdecea' },
  snow: { label: 'ServiceNow', icon: ServiceNowIcon, color: INCIDENT_ACCENT, bg: '#fdecea' }
};

const isClosedState = (state) =>
  /\b(closed|done|completed|resolved|removed|cancelled|canceled)\b/i.test(String(state || '').trim());

const formatDuration = (ms) => {
  if (ms == null || Number.isNaN(ms) || ms < 0) return '—';
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${Math.max(1, minutes)}m`;
};

/** Age: created -> closed/done date, or -> now while the ticket is still open. */
const timeInStage = (item) => {
  const start = item.createdDate ? new Date(item.createdDate).getTime() : NaN;
  if (Number.isNaN(start)) return null;
  const closed = isClosedState(item.state);
  const endRaw = item.closedDate || (closed ? item.changedDate : null);
  const end = endRaw ? new Date(endRaw).getTime() : Date.now();
  return { text: formatDuration(end - start), ongoing: !endRaw };
};

const DetailRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 0.6 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, textAlign: 'right' }}>{children}</Box>
  </Box>
);

const StageBox = ({ stage, typeLabel, item, onClick, emptyText }) => {
  const style = STAGE_STYLES[stage];
  const Icon = style.icon;
  const age = item ? timeInStage(item) : null;
  return (
    <Paper
      elevation={item ? 2 : 0}
      onClick={onClick}
      title={onClick ? 'Open details' : undefined}
      sx={{
        ...(onClick ? { cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 6 } } : null),
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(item ? null : { border: '1px dashed rgba(0,0,0,0.3)', bgcolor: '#eceff1' })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          minHeight: 44,
          background: item
            ? `linear-gradient(135deg, ${style.color}ee 0%, ${style.color}bb 100%)`
            : 'linear-gradient(135deg, #b0bec5 0%, #90a4ae 100%)'
        }}
      >
        <Icon sx={{ color: '#fff', fontSize: 18, flexShrink: 0 }} />
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8125rem', letterSpacing: 0.3 }}>
          {typeLabel ? `${style.label} (${typeLabel})` : style.label}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: item ? '#fff' : '#eceff1', px: 1.75, py: 1.25, flexGrow: 1 }}>
        {!item ? (
          <Typography variant="body2" sx={{ color: MUTED, py: 2, textAlign: 'center' }}>
            {emptyText || (stage === 'l2' ? 'No L2 ticket linked' : 'No L3 ticket linked')}
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 1 }}>
              <CaseIdLink value={item.id} href={item.url} color={style.color} />
              <Typography sx={{ color: INK, fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3, mt: 0.25 }}>
                {item.title || '—'}
              </Typography>
            </Box>
            <DetailRow label="Created">
              <Typography sx={{ fontSize: '0.8125rem', color: INK }}>{formatSfTableDate(item.createdDate)}</Typography>
            </DetailRow>
            {item.showModified && (
              <DetailRow label="Modified">
                <Typography sx={{ fontSize: '0.8125rem', color: INK }}>{formatSfTableDate(item.modifiedDate)}</Typography>
              </DetailRow>
            )}
            {item.showClosed && (
              <DetailRow label="Closed">
                <Typography sx={{ fontSize: '0.8125rem', color: item.closedDate ? INK : MUTED }}>
                  {formatSfTableDate(item.closedDate)}
                </Typography>
              </DetailRow>
            )}
            <DetailRow label="Status">
              <Chip
                size="small"
                label={item.state || '—'}
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: isClosedState(item.state) ? NEUTRAL_SURFACE : style.bg,
                  color: isClosedState(item.state) ? MUTED : style.color
                }}
              />
            </DetailRow>
            {item.stage && (
              <DetailRow label="Stage">
                <Typography sx={{ fontSize: '0.8125rem', color: INK }}>{item.stage}</Typography>
              </DetailRow>
            )}
            <DetailRow label="Assignee">
              <AssigneeCell
                assignedTo={isUnassigned(item.assignedTo) ? '' : item.assignedTo}
                color={style.color}
                bg={style.bg}
                emptyLabel="Unassigned"
              />
            </DetailRow>
            {item.showFixVersion && (
              <DetailRow label="Fix version">
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: item.fixVersion ? 700 : 400, color: item.fixVersion ? INK : MUTED }}>
                  {item.fixVersion || '—'}
                </Typography>
              </DetailRow>
            )}
            <DetailRow label="Age">
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: style.color }}>
                {age ? age.text : '—'}
                {age && age.ongoing && (
                  <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 500, color: MUTED, ml: 0.5 }}>
                    (ongoing)
                  </Typography>
                )}
              </Typography>
            </DetailRow>
          </>
        )}
      </Box>
    </Paper>
  );
};

/** One-way arrow to the next stage (or back to the case when reversed); greyed when that stage has no ticket. */
const JourneyArrow = ({ color, active, reverse }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      px: { xs: 0, md: 0.5 },
      py: { xs: 0.5, md: 0 },
      transform: { xs: reverse ? 'rotate(-90deg)' : 'rotate(90deg)', md: reverse ? 'rotate(180deg)' : 'none' }
    }}
  >
    <ArrowForwardIcon sx={{ fontSize: 32, color: active ? color : 'rgba(0,0,0,0.25)' }} />
  </Box>
);

/** Dashed line: the item on its right is related to the item on its left, not linked on it. */
const RelatedConnector = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, px: { md: 0.5 } }}>
    <Box
      title="Related"
      sx={{
        width: { xs: 0, md: 36 },
        height: { xs: 20, md: 0 },
        borderTop: { md: '2px dashed rgba(0,0,0,0.45)' },
        borderLeft: { xs: '2px dashed rgba(0,0,0,0.45)', md: 0 }
      }}
    />
  </Box>
);

// Explicit grid placement on md+; stacked in source order on small screens.
const cell = (column, row) => ({ gridColumn: { md: column }, gridRow: { md: row }, minWidth: 0 });
const arrowCellSx = { alignSelf: { md: 'start' }, pt: { md: 6 } };

/** Click handler for an L2 / L3 box: opens that ADO ticket's details. */
const adoBoxClick = (stage, item, open) => (event) => {
  if (event.target.closest('a, button')) return;
  open({ id: item.id, url: item.url, stage });
};

const L3_TYPE_NAMES = new Set(['Bug', 'Product Backlog Item', 'Feature']);
const stageOfType = (type) => (L3_TYPE_NAMES.has(type) ? 'l3' : 'l2');

const isSnowLinked = (ticket) => ticket.linkType === 'Incident' || ticket.linkType === 'Service';

/** Full case details (same panel as the Top Customers view), loaded when the L1 box is clicked. */
export const CaseDetailsDialog = ({ caseNumber, onClose }) => {
  const [state, setState] = useState({ loading: true, ticket: null, error: '', source: '' });
  React.useEffect(() => {
    let cancelled = false;
    setState({ loading: true, ticket: null, error: '', source: '' });
    authGet(`/api/customer-issues/case-details/${encodeURIComponent(caseNumber)}`)
      .then((res) => {
        if (cancelled) return;
        if (res && res.success && res.ticket) setState({ loading: false, ticket: res.ticket, error: '', source: res.source || '' });
        else setState({ loading: false, ticket: null, error: (res && res.message) || 'Case details not found.', source: '' });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, ticket: null, error: (err && err.message) || 'Failed to load case details.', source: '' });
      });
    return () => { cancelled = true; };
  }, [caseNumber]);

  const sfUrl = absoluteHttpUrl(state.ticket && state.ticket.sfUrl);
  const sourceLabel = { mongo: 'synced copy', snowflake: 'live query' }[state.source];

  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 700 }}>
        Case details · {caseNumber}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {sfUrl && (
            <Button
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              href={sfUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Open in Salesforce
            </Button>
          )}
          <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>Close</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: NEUTRAL_SURFACE }}>
        {state.loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>
              Retrieving case {caseNumber} from the Salesforce data lake…
            </Typography>
          </Box>
        )}
        {state.error && <Alert severity="error">{state.error}</Alert>}
        {state.ticket && (
          <>
            {sourceLabel && (
              <Typography sx={{ fontSize: '0.75rem', color: MUTED, mb: 1 }}>
                Source: Salesforce data lake (Snowflake), {sourceLabel}
              </Typography>
            )}
            <TicketFollowUpPanel ticket={state.ticket} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TicketJourneyDialog = ({ open, onClose, ticket }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [adoDetails, setAdoDetails] = useState(null);
  const [snowDetails, setSnowDetails] = useState(null);
  if (!ticket) return null;
  const handleClose = () => {
    setDetailsOpen(false);
    setAdoDetails(null);
    setSnowDetails(null);
    onClose();
  };
  const l1Item = {
    id: ticket.id,
    title: ticket.title,
    state: ticket.state,
    stage: ticket.stage,
    assignedTo: ticket.assignedTo,
    createdDate: ticket.createdDate,
    modifiedDate: ticket.lastModifiedDate,
    showModified: true,
    url: ticket.sfUrl
  };
  // A case tied to ServiceNow skips L2/L3: one ServiceNow box instead.
  const snowItem = isSnowLinked(ticket) ? {
    id: ticket.snowId || ticket.externalId || (ticket.linkType === 'Service' ? 'RITM' : 'Incident'),
    title: ticket.snowTitle,
    state: ticket.snowState,
    assignedTo: ticket.snowAssignedTo,
    createdDate: ticket.snowCreatedDate,
    closedDate: ticket.snowClosedDate,
    url: ticket.snowUrl || ticket.url,
    showClosed: true
  } : null;
  const withDetails = (item) => ({ ...item, showClosed: true, showFixVersion: stageOfType(item.type) === 'l3' });
  // Linked: the ticket the Salesforce case itself links to. Related: every other ticket tied to the case, each
  // hanging off the item it is related to (relatedTo = '' means the Salesforce case).
  const linkedItem = snowItem || (ticket.linkedAdo ? withDetails(ticket.linkedAdo) : null);
  const linkedStage = snowItem ? 'snow' : (linkedItem ? stageOfType(linkedItem.type) : 'l2');
  const related = (ticket.relatedItems || []).map(withDetails);
  // Tree: the linked ticket and the tickets tied straight to the case are roots; every other ticket hangs off
  // the one it is related to (at any depth). Each tree level is one column, each leaf one row.
  const placed = new Set([linkedItem && linkedItem.id].filter(Boolean));
  const relatedIds = new Set(related.map((item) => item.id));
  const childrenOf = new Map();
  const treeRoots = [];
  related.forEach((item) => {
    const parent = item.relatedTo;
    const parentShown = parent && (parent === (linkedItem && linkedItem.id) || relatedIds.has(parent)) && parent !== item.id;
    if (!parentShown) treeRoots.push(item);
    else childrenOf.set(parent, [...(childrenOf.get(parent) || []), item]);
  });
  const placements = [];
  let nextRow = 1;
  let maxDepth = 0;
  const place = (item, stage, linked, depth) => {
    placed.add(item.id);
    maxDepth = Math.max(maxDepth, depth);
    const start = nextRow;
    const entry = { item, stage, linked, depth, start, span: 1 };
    placements.push(entry);
    const children = (childrenOf.get(item.id) || []).filter((child) => !placed.has(child.id));
    if (children.length === 0) {
      nextRow += 1;
    } else {
      children.forEach((child) => place(child, stageOfType(child.type), false, depth + 1));
    }
    entry.span = nextRow - start;
  };
  if (linkedItem) place(linkedItem, linkedStage, true, 0);
  treeRoots.filter((item) => !placed.has(item.id)).forEach((item) => place(item, stageOfType(item.type), false, 0));
  const totalRows = Math.max(1, nextRow - 1);
  const openAdo = (stage, item) => adoBoxClick(stage, item, setAdoDetails);
  const openSnow = (item) => (event) => {
    if (event.target.closest('a, button')) return;
    setSnowDetails({ number: item.id, url: item.url });
  };
  const boxClick = (stage, item) => {
    if (stage !== 'snow') return openAdo(stage, item);
    return SNOW_NUMBER.test(String(item.id || '')) ? openSnow(item) : undefined;
  };
  const spanSx = (column, start, span) => ({
    ...cell(column, `${start} / span ${span}`),
    display: 'flex',
    alignItems: 'center',
    '& > *': { width: '100%' }
  });

  return (
    // 1500px with three stage columns (1000px with two); never wider than the viewport.
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: 1000 + 500 * Math.min(maxDepth, 2), maxWidth: '96vw', maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
          Ticket Journey · Case {ticket.id}
        </Typography>
        <IconButton aria-label="Close" onClick={handleClose} size="small" sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: NEUTRAL_SURFACE }}>
        {/* Salesforce case on the left; its linked ticket joined by a solid arrow, related tickets by dashed lines. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `1fr${' auto 1fr'.repeat(maxDepth + 1)}` },
            gridAutoRows: { md: 'minmax(0, auto)' },
            columnGap: 1,
            rowGap: { xs: 0.5, md: 1.5 }
          }}
        >
          <Box sx={{ ...cell(1, `1 / span ${totalRows}`), display: 'flex', '& > *': { width: '100%' } }}>
            <StageBox stage="l1" item={l1Item} onClick={(event) => {
              if (event.target.closest('a, button')) return;
              setDetailsOpen(true);
            }} />
          </Box>
          {placements.length === 0 && (
            <>
              <Box sx={{ ...cell(2, 1), ...arrowCellSx }}>
                <JourneyArrow color={STAGE_STYLES.l2.color} active={false} />
              </Box>
              <Box sx={cell(3, 1)}>
                <StageBox stage="l2" item={null} emptyText="No linked ticket" />
              </Box>
            </>
          )}
          {placements.map((row) => (
            <React.Fragment key={row.item.id}>
              <Box sx={{ ...cell(2 + 2 * row.depth, `${row.start} / span ${row.span}`), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {row.linked || row.item.viaZendesk
                  ? <JourneyArrow color={STAGE_STYLES[row.stage].color} active reverse={!row.linked} />
                  : <RelatedConnector />}
              </Box>
              <Box sx={spanSx(3 + 2 * row.depth, row.start, row.span)}>
                <StageBox
                  stage={row.stage}
                  typeLabel={row.stage === 'snow' ? '' : row.item.type}
                  item={row.item}
                  onClick={boxClick(row.stage, row.item)}
                />
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>
      {detailsOpen && <CaseDetailsDialog caseNumber={ticket.id} onClose={() => setDetailsOpen(false)} />}
      {snowDetails && (
        <SnowTicketDetailsDialog
          number={snowDetails.number}
          fallbackUrl={snowDetails.url}
          color={STAGE_STYLES.snow.color}
          onClose={() => setSnowDetails(null)}
        />
      )}
      {adoDetails && (
        <AdoItemDetailsDialog
          workItemId={adoDetails.id}
          fallbackUrl={adoDetails.url}
          color={STAGE_STYLES[adoDetails.stage].color}
          onClose={() => setAdoDetails(null)}
        />
      )}
    </Dialog>
  );
};

export default TicketJourneyDialog;
